'use client';
import { useEffect, useState } from 'react';

import useHash from '@/hooks/useHash';

import { LEADS_COLLECTION_URL_HASH } from '@/constant/urls';
import Section from '@/components/atoms/Section/Section';
import { ErrorPage } from '@/features/ContactSales/ErrorPage';
import { SuccessPage } from '@/features/ContactSales/SuccessPage';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';

import { ImageStrapiProps } from '../../atoms/Image/ImageStrapi';
import { DropdownOption } from '../../atoms/InputDropdownWithImage';
import { Modal } from '../../atoms/Modal';
import { RichTextFieldStrapiProps } from '../../atoms/RichTextField/RichTextFieldStrapi';
import { SubmissionStatusMessage } from '../SubmissionStatusPopUp';
import { LeadPopUp } from './LeadPopUpForm';

export type LeadPopUpProps = {
  urlHash: string;
};

type DropdownOptions = {
  dropDownLabel: string;
  dropdownOptions: DropdownOption[];
};

export type CaptureLeadPopupProps = {
  activeCampaignListId?: number;
  gateIdentifier: string;
  saveExistingEmails?: boolean;
  image: ImageStrapiProps;
  multipleInputs?: {
    fieldLabel: string;
    fieldMapping: string;
    isRequired: boolean;
  }[];
  leadsCollection?: 'ActiveCampaign' | 'SalesForce';
  openOnPageLoad?: boolean;
  title: RichTextFieldStrapiProps;
  description: RichTextFieldStrapiProps;
  submitButtonText: RichTextFieldStrapiProps;
  termsAndConditions: RichTextFieldStrapiProps;
  dropdownOptions?: DropdownOptions;
  localizations: {
    data: Array<any>;
  };
};

type LeadPopUpStrapiData = {
  data?: {
    attributes: CaptureLeadPopupProps;
  };
};

type LeadspopupSteps = 'lead_capture_form' | 'success_page' | 'error_page';

export function LeadPopUpModal({
  lead_pop_up,
}: {
  lead_pop_up?: LeadPopUpStrapiData;
}) {
  const { hash, clearHash } = useHash();
  const isHashTriggered = hash === LEADS_COLLECTION_URL_HASH;
  const openOnPageLoad =
    lead_pop_up?.data?.attributes?.openOnPageLoad !== false;

  const [isOpen, setIsOpen] = useState(openOnPageLoad);
  const [step, setStep] = useState<LeadspopupSteps>('lead_capture_form');
  const [submitState, setSubmitState] = useState<
    'idle' | 'success' | 'invalid' | 'error'
  >('idle');

  useEffect(() => {
    if (isHashTriggered) {
      setIsOpen(true);
      setStep('lead_capture_form');
    }
  }, [isHashTriggered]);

  function handleClose() {
    setIsOpen(false);
    setSubmitState('idle');
    if (isHashTriggered) {
      clearHash();
    }
  }

  function handleSubmitResult(result: 'success' | 'invalid' | 'error') {
    setSubmitState(result);

    if (result === 'success') {
      setStep('success_page');
      setSubmitState('idle');
    } else if (result === 'error') {
      setSubmitState('idle');
      setStep('error_page');
    } else if (result === 'invalid') {
      setTimeout(() => {
        setSubmitState('idle');
      }, 4000);
    } else {
      setStep('lead_capture_form');
    }
  }

  function handleTryAgain() {
    setSubmitState('idle');
    setStep('lead_capture_form');
  }

  return (
    <Section data-tracker-section={SECTION_NAMES.LEAD_COLLECTION_FORM}>
      <Modal
        data-tracker-section={SECTION_NAMES.LEAD_COLLECTION_FORM}
        isOpen={isOpen}
        onClose={handleClose}
        closeOnOutsideClick={true}
        hasClose={true}
        overlayProps={{ fullScreen: true, darkOverlay: true }}
        classNames={{
          overlay: 'z-[61]',
          close: 'text-gray-500 m-2 p-3 hover:bg-gray-50 active:bg-gray-200',
          closeCross: 'h-6 w-6',
          modal:
            'px-5 p-6 mx-5 w-full md:w-auto md:my-auto rounded-xl md:max-w-[838px] border-0 shadow',
          root: 'z-[62] py-16 items-start overflow-y-auto',
        }}
      >
        <SubmissionStatusMessage
          status={submitState}
          onClose={() => setSubmitState('idle')}
          className='light'
        />
        {lead_pop_up?.data?.attributes && (
          <>
            {step === 'lead_capture_form' && (
              <LeadPopUp
                {...lead_pop_up.data.attributes}
                onSubmitResult={handleSubmitResult}
              />
            )}
            {step === 'success_page' && <SuccessPage isModal={true} />}
            {step === 'error_page' && (
              <ErrorPage onSubmit={handleTryAgain} isModal={true} />
            )}
          </>
        )}
      </Modal>
    </Section>
  );
}
